import { useState } from "react";
import { ShoppingBag } from "@phosphor-icons/react";
import CollapsibleSection from "@/components/admin/CollapsibleSection";
import { MerchAccounting } from "@/pages/admin/MerchAccounting";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const badge = (label, tone) => (
  <span className={`inline-block px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] font-bold border ${
    tone === "good" ? "border-emerald-500 text-emerald-300 bg-emerald-500/10" :
    tone === "warn" ? "border-yellow-500 text-yellow-300 bg-yellow-500/10" :
    tone === "bad" ? "border-red-500 text-red-300 bg-red-500/10" :
    "border-white/25 text-white/60"
  }`}>{label}</span>
);

// Order stages surfaced to the admin. Printful drafts are approved on
// Printful.com directly — the SWS admin does not gate fulfillment — so
// the "awaiting_approval" stage was retired from the UI in 2026-03-05.
const STAGE_ORDER = ["paid", "in_fulfillment", "shipped"];
const STAGE_LABELS = {
  pending: "Pending",
  paid: "Paid",
  in_fulfillment: "Printing",
  shipped: "Shipped",
};

function ProgressStepper({ order }) {
  const stage = order.stage || "pending";
  // Draft orders still report `stage: 'awaiting_approval'` from the
  // backend for legacy reasons — treat them as "paid" for the UI so the
  // stepper doesn't imply the admin needs to act.
  const uiStage = stage === "awaiting_approval" ? "paid" : stage;
  const stages = STAGE_ORDER;
  const currentIdx = stages.indexOf(uiStage);
  const tracking = order.tracking || {};
  return (
    <div data-testid={`stepper-${order.id}`} className="flex items-center gap-1 mt-2">
      {stages.map((s, i) => {
        const done = currentIdx >= 0 && i <= currentIdx;
        const isCurrent = i === currentIdx;
        const dotClass = done ? "bg-emerald-500 border-emerald-500" : "bg-transparent border-white/25";
        const labelClass = done ? "text-emerald-300" : "text-white/40";
        return (
          <div key={s} className="flex items-center gap-1" title={STAGE_LABELS[s]}>
            <span className={`w-2 h-2 rounded-full border-2 ${dotClass} ${isCurrent ? "ring-2 ring-offset-1 ring-offset-[#0F0F0F] ring-white/20" : ""}`} />
            <span className={`text-[8px] uppercase tracking-[0.15em] font-bold ${labelClass}`}>{STAGE_LABELS[s]}</span>
            {i < stages.length - 1 && <span className={`w-3 h-px ${done && i < currentIdx ? "bg-emerald-500" : "bg-white/15"}`} />}
          </div>
        );
      })}
      {stage === "shipped" && tracking.tracking_url && (
        <a href={tracking.tracking_url} target="_blank" rel="noreferrer noopener" className="ml-2 text-[9px] uppercase tracking-[0.15em] font-bold text-emerald-300 hover:text-emerald-200 border-b border-emerald-500/50">
          {tracking.carrier || "Track"} →
        </a>
      )}
    </div>
  );
}

export default function MerchOrdersPanel({ token, data, refresh }) {
  const orders = data?.orders || [];
  const [retrying, setRetrying] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [purging, setPurging] = useState(false);
  const [remindersSending, setRemindersSending] = useState(false);
  const [msg, setMsg] = useState("");

  const deleteOrder = async (orderId, label) => {
    if (!window.confirm(
      `Delete internal merch-order record for ${label}?\n\n` +
      `This ONLY removes the local DB row.\n\n` +
      `It will NOT refund Square/Stripe.\n` +
      `It will NOT cancel or delete the Printful order.\n\n` +
      `Cannot be undone.`
    )) return;
    setDeleting(orderId);
    setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/merch-orders/${orderId}`, {
        method: "DELETE",
        headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Delete failed");
      setMsg(`Deleted order record. Refreshing…`);
      await refresh();
    } catch (e) {
      setMsg(`Delete failed: ${e.message}`);
    } finally {
      setDeleting(null);
    }
  };

  const purgeTestData = async () => {
    if (!window.confirm(
      `Purge ALL synthetic/test/probe merch-order records?\n\n` +
      `Deletes:\n` +
      `  • Every order flagged is_test=true\n` +
      `  • Every unpaid probe order (verify.*, speed.*, timing.*, latency.*, debug-*, audit.*, one.item.verify, no.dedication, test.buyer, seed.paid, *@example.com)\n\n` +
      `NEVER touches a legitimate paid customer order.\n` +
      `NEVER refunds anything.\n\n` +
      `Cannot be undone. Continue?`
    )) return;
    setPurging(true);
    setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/merch-orders/purge-test-data`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Purge failed");
      setMsg(`Purged ${d.deleted} test/probe records. Refreshing…`);
      await refresh();
    } catch (e) {
      setMsg(`Purge failed: ${e.message}`);
    } finally {
      setPurging(false);
    }
  };

  const retry = async (orderId) => {
    setRetrying(orderId);
    setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/merch-orders/${orderId}/retry-printful`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Retry failed");
      setMsg("Retry pushed. Refreshing…");
      await refresh();
    } catch (e) {
      setMsg(`Retry failed: ${e.message}`);
    } finally {
      setRetrying(null);
    }
  };

  const sendReminders = async () => {
    if (remindersSending) return;
    setRemindersSending(true);
    setMsg("");
    try {
      const r = await fetch(`${BACKEND}/api/admin/wall-reminders/send`, {
        method: "POST",
        headers: { "X-Admin-Token": token },
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Reminder sweep failed");
      setMsg(`Sent ${d.reminders_sent} wall reminder${d.reminders_sent === 1 ? "" : "s"}.`);
    } catch (e) {
      setMsg(`Reminder sweep failed: ${e.message}`);
    } finally {
      setRemindersSending(false);
    }
  };

  // Operational-only badges — total/paid/revenue live in the top
  // Dashboard panel and were removed here to eliminate duplication.
  // What stays: draft count (informational — approved on Printful.com),
  // in-fulfillment, shipped, failed. All are things that MIGHT need eyes.
  const badges = (
    <div className="flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
      {(data?.stage_counts?.awaiting_approval || 0) > 0 && badge(`${data.stage_counts.awaiting_approval} drafts in Printful`, "warn")}
      {(data?.stage_counts?.in_fulfillment || 0) > 0 && badge(`${data.stage_counts.in_fulfillment} printing`, "warn")}
      {(data?.stage_counts?.shipped || 0) > 0 && badge(`${data.stage_counts.shipped} shipped`, "good")}
      {data?.printful_failed_count > 0 && badge(`${data.printful_failed_count} failed`, "bad")}
    </div>
  );

  const reminderBtn = (
    <div className="inline-flex gap-2">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); sendReminders(); }}
        disabled={remindersSending}
        data-testid="admin-send-wall-reminders"
        className="text-[10px] uppercase tracking-[0.25em] font-bold border-2 border-[#FF1B91] hover:bg-[#FF1B91]/20 text-white px-3 py-1.5 disabled:opacity-40"
        title="Emails merch buyers who haven't added a name to the Legacy Wall (24h+ after purchase). Idempotent — only sends once per order."
      >
        {remindersSending ? "Sending…" : "Send wall reminders"}
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); purgeTestData(); }}
        disabled={purging}
        data-testid="admin-purge-test-data"
        className="text-[10px] uppercase tracking-[0.25em] font-bold border-2 border-red-500/60 hover:bg-red-500/20 text-red-300 hover:text-red-200 px-3 py-1.5 disabled:opacity-40"
        title="Deletes all is_test=true rows + unpaid synthetic probe rows. Never touches legitimate paid orders."
      >
        {purging ? "Purging…" : "Purge test data"}
      </button>
    </div>
  );

  return (
    <CollapsibleSection
      id="merch-orders"
      testId="admin-merch-orders"
      title={<><ShoppingBag size={18} weight="fill" className="text-[#FF1B91] inline mr-2" />Merch Orders (Stripe / Square → Printful)</>}
      badge={badges}
      onRefresh={refresh}
      extraActions={reminderBtn}
    >
      <p className="text-white/45 text-[11px] mb-3 italic">
        Paid orders auto-route to Printful as manual-fulfillment drafts. Approve, edit, or hold each order directly on Printful.com — this dashboard does not gate fulfillment.
      </p>

      <MerchAccounting token={token} />

      {msg && <div className="px-1 py-2 text-[11px] uppercase tracking-[0.2em] text-white/60">{msg}</div>}

      {orders.length === 0 ? (
        <div className="p-8 text-center text-white/50 text-sm">
          No merch orders yet. Once a customer completes Stripe checkout, orders land here + auto-route to Printful.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 text-white/50 uppercase tracking-[0.2em] text-[10px]">
                <th className="text-left p-3 font-bold">Customer</th>
                <th className="text-left p-3 font-bold">Product</th>
                <th className="text-left p-3 font-bold">Total</th>
                <th className="text-left p-3 font-bold">Payment</th>
                <th className="text-left p-3 font-bold">Printful</th>
                <th className="text-left p-3 font-bold">Ship to</th>
                <th className="text-left p-3 font-bold">Created</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const paid = o.payment_status === "paid";
                const shipped = !!o.printful_order_id;
                const failed = o.printful_error && !o.printful_order_id;
                const isDraft = !!o.printful_is_draft && !shipped ? false : !!o.printful_is_draft;
                return (
                  <tr key={o.id} data-testid={`merch-order-${o.id}`} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3">
                      <p className="text-white font-semibold">{o.recipient?.name || "—"}</p>
                      <p className="text-white/45 text-[10px]">{o.recipient?.email || "—"}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-white line-clamp-2 max-w-[240px]">{o.product_name}</p>
                      <p className="text-white/45 text-[10px]">{o.variant_name} · Qty {o.quantity}</p>
                      <ProgressStepper order={o} />
                    </td>
                    <td className="p-3 text-white font-bold">${Number(o.total || 0).toFixed(2)}</td>
                    <td className="p-3">
                      {paid ? badge("Paid", "good") : badge(o.payment_status || "pending", "warn")}
                      <p
                        data-testid={`merch-order-provider-${o.id}`}
                        className={`mt-1.5 text-[9px] uppercase tracking-[0.2em] font-bold ${o.payment_provider === "square" ? "text-[#22C55E]" : "text-[#8A5CFF]"}`}
                        title={o.payment_provider === "square" ? o.square_payment_id || "—" : o.stripe_session_id || "—"}
                      >
                        Paid with {o.payment_provider === "square" ? "Square" : (o.payment_provider === "stripe" || o.stripe_session_id) ? "Stripe" : "—"}
                      </p>
                    </td>
                    <td className="p-3">
                      {isDraft ? (
                        <div>
                          {badge("Draft in Printful", "warn")}
                          <p className="text-white/50 text-[10px] mt-1">
                            #{o.printful_order_id} · <a href="https://www.printful.com/dashboard/default/orders" target="_blank" rel="noreferrer noopener" className="underline hover:text-white">approve on Printful</a>
                          </p>
                        </div>
                      ) : shipped ? (
                        <div>
                          {badge(o.printful_status || "confirmed", "good")}
                          <p className="text-white/45 text-[10px] mt-1">#{o.printful_order_id}</p>
                        </div>
                      ) : failed ? (
                        <div>
                          {badge("Failed", "bad")}
                          <p className="text-red-300 text-[10px] mt-1 max-w-[200px] line-clamp-2" title={o.printful_error}>
                            {String(o.printful_error).slice(0, 80)}
                          </p>
                        </div>
                      ) : paid ? (
                        badge("Queued", "warn")
                      ) : (
                        badge("—")
                      )}
                    </td>
                    <td className="p-3 text-white/70 text-[10px] max-w-[180px]">
                      {o.recipient?.address1}<br/>
                      {o.recipient?.city}, {o.recipient?.state_code} {o.recipient?.zip}
                    </td>
                    <td className="p-3 text-white/45 text-[10px]">{(o.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="p-3 space-y-1">
                      {paid && !shipped && !isDraft && (
                        <button
                          onClick={() => retry(o.id)}
                          disabled={retrying === o.id}
                          data-testid={`retry-printful-${o.id}`}
                          className="w-full text-[9px] uppercase tracking-[0.2em] font-bold border border-[#FF1B91] hover:bg-[#FF1B91]/20 text-white px-2 py-1 disabled:opacity-40"
                        >
                          {retrying === o.id ? "…" : "Retry Printful"}
                        </button>
                      )}
                      <button
                        onClick={() => deleteOrder(o.id, `${o.recipient?.email || 'unknown'} · $${o.total}`)}
                        disabled={deleting === o.id}
                        data-testid={`delete-order-${o.id}`}
                        className="w-full text-[9px] uppercase tracking-[0.2em] font-bold border border-red-500/50 hover:bg-red-500/20 text-red-300 hover:text-red-200 px-2 py-1 disabled:opacity-40"
                      >
                        {deleting === o.id ? "Deleting…" : "Delete record"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </CollapsibleSection>
  );
}
